import ArticleCategory from '../smoothr-web-app-core/models/ArticleCategory';
import { defaultsToArticleOption, getCustomAvailability } from '../smoothr-web-app-core/utils/utils';
import { OrderType } from '../smoothr-web-app-core/enums/OrderType';
import ArticleGroup from '../smoothr-web-app-core/models/ArticleGroup';
import { ModalInfoComponent } from './components/modal-info/modal-info.component';
import { OrderUtils } from '../smoothr-web-app-core/utils/order-utils';
import Article from '../smoothr-web-app-core/models/Article';
import { RecomendationArticleModalComponent } from './components/recomendation-article-modal/recomendation-article-modal.component';
import { ValidationUtils } from '../smoothr-web-app-core/utils/validation-utils';
import { ModalController } from '@ionic/angular';
import { OrderContentModalComponent } from './components/order-content-modal/order-content-modal.component';
import { RepositoryService } from '../smoothr-web-app-core/services/repository/repository.service';
import Order from '../smoothr-web-app-core/models/Order';
import { environment } from '../environments/environment';
import { DoubleMondayModal, DoubleMondayModalResult } from './components/double-monday-modal/double-monday-modal';
import { SmoothrCNCDiscount } from '../smoothr-web-app-core/models/CNCDiscount';

export async function toggleBasket(modalCtrl: ModalController) {
	await OrderContentModalComponent.presentModal(modalCtrl);
}

export function articleNeedsCustomization(order: Order, article: Article): boolean {
	if (!article) {
		return false;
	}

	// Articles with special tags always need customization
	if (article.tags && article.tags.length > 0) {
		const specialTags = ['custom_bowl', 'create_own', 'half_pizza'];
		if (article.tags.some(tag => specialTags.includes(tag.identifier))) {
			return true;
		}
	}

	if (!article.groups || article.groups.length === 0) {
		return false;
	}

	const hasCustomizableGroups = article.groups.some(group => {
		// Skip non-visible groups
		if (!group.visible) {
			return false;
		}

		// Skip groups with no articles
		if (!group.articles || group.articles.length === 0) {
			return false;
		}

		// Check if group has articles that are visible, active, and available
		const availableArticles = group.articles.filter(a => {
			if (!a || !a.visible || !a.isActive) {
				return false;
			}
			return getCustomAvailability(a, OrderType.STANDARD, order?.preorder?.type, null, true, false, true);
		});

		// If no articles are available, this group doesn't need customization
		if (availableArticles.length === 0) {
			return false;
		}

		// Check if this group requires user interaction
		const hasRequiredSelection = group.requiredAmount > 0;
		const canSelectItems = group.limit > 0;
		const isSingleAutomatic = group.limit === 1 && group.requiredAmount === 1 && availableArticles.length === 1;

		// Only needs customization if it has requirements/limits and is not auto-selected
		return (hasRequiredSelection || canSelectItems) && !isSingleAutomatic;
	});

	console.log('articleNeedsCustomization for', article.name, ':', hasCustomizableGroups, {
		groups: article.groups.map(g => ({
			name: g.name,
			visible: g.visible,
			articlesCount: g.articles?.length || 0,
			requiredAmount: g.requiredAmount,
			limit: g.limit,
		})),
	});

	return hasCustomizableGroups;
}

export function removeBasket(repositoryService: RepositoryService, article: Article) {
	const order = repositoryService._order;
	console.log('REMOVE', article);
	const findIndex = order.orderedArticles.findIndex(it => it.article._id === article._id);
	console.log('found index', findIndex);
	if (findIndex >= 0) {
		const result = order.orderedArticles[findIndex];
		if (result.quantity > 1) {
			order.orderedArticles[findIndex].quantity -= 1;
		} else {
			order.orderedArticles = order.orderedArticles.filter((it, index) => index !== findIndex);
		}
	}
	console.log(order);
	repositoryService.onOrderChange({ ...order });
}

export async function addToBasket(
	modalCtrl: ModalController,
	repositoryService: RepositoryService,
	item: Article,
	category: ArticleCategory,
	addRecomendations: boolean = false,
	isAddedToBasket: boolean = false
) {
	if (item?.tags && item.tags.length > 0 && item.tags.find(it => it.identifier === 'custom_bowl')) {
		isAddedToBasket = false;
	}
	if (!isAddedToBasket) {
		await openArticleEditModal(modalCtrl, repositoryService, item, category, addRecomendations);
		return;
	}

	const order = structuredClone(repositoryService._order);
	// Check if article actually needs customization
	if (articleNeedsCustomization(order, item)) {
		await openArticleEditModal(modalCtrl, repositoryService, item, category, addRecomendations);
		return;
	}

	const articleGroup = new ArticleGroup();
	articleGroup.article = item;
	articleGroup.groups.push(...defaultsToArticleOption(item, [], item.defaults, order?.type, order.preorder.type));
	articleGroup.quantity = 1;
	articleGroup.categoryName = category?.name;

	if (ValidationUtils.areGroupsValid(articleGroup, [])) {
		OrderUtils.addToOrder(order, articleGroup);
		repositoryService.onOrderChange({ ...order });

		// showNotification(
		// 	translate.instant('add_to_basket.start') +
		// 		articleGroup.article.name.de +
		// 		translate.instant('add_to_basket.end')
		// );
	} else {
		await openArticleEditModal(modalCtrl, repositoryService, item, category, addRecomendations);
	}
}

async function handleRecommendations(modalCtrl: ModalController, repositoryService: RepositoryService, item: Article, order: any) {
	const mainRecommendations = item.mainRecommendations;
	if (!mainRecommendations || mainRecommendations.length === 0) {
		return;
	}

	let categoryArticles = repositoryService._venue.articleCategories.flatMap(it => it.articles) as Article[];
	// categoryArticles = categoryArticles.filter(article => article.visible && article.isActive);
	const categoryArticlesMap = new Map(categoryArticles.map(it => [it._id, it]));

	const recommendedArticles: Article[] = mainRecommendations.map(id => categoryArticlesMap.get(id));
	// .filter(article => article && article.visible);

	if (recommendedArticles.length > 0) {
		const result = await RecomendationArticleModalComponent.show(modalCtrl, recommendedArticles);
		if (result?.data?.length > 0) {
			result.data.forEach(itemRecom => {
				const articleGroup = new ArticleGroup();
				articleGroup.article = itemRecom.article;
				articleGroup.groups.push(
					...defaultsToArticleOption(itemRecom.article, [], itemRecom.article.defaults, order?.type, order.preorder.type)
				);
				articleGroup.quantity = itemRecom.quantity;
				OrderUtils.addToOrder(order, articleGroup);
				repositoryService.onOrderChange({ ...order });
			});
		}
	}
}

export function createArticleGroup(item: Article, category: ArticleCategory, order: any): ArticleGroup {
	const articleGroup = new ArticleGroup();
	articleGroup.article = item;
	articleGroup.groups.push(...defaultsToArticleOption(item, [], item.defaults, order?.type, order.preorder?.type));
	articleGroup.quantity = 1;
	articleGroup.categoryName = category?.name;
	return articleGroup;
}

export async function handleModalResult(
	result: ArticleGroup,
	order: Order,
	repositoryService: RepositoryService,
	addRecommendations: boolean,
	modalCtrl: ModalController,
	item: Article
) {
	if (result) {
		OrderUtils.addToOrder(order, result);
		repositoryService.onOrderChange({ ...order });
	}
	if (result && addRecommendations) {
		await handleRecommendations(modalCtrl, repositoryService, item, order);
	}
}

export async function openArticleEditModal(
	modalCtrl: ModalController,
	repositoryService: RepositoryService,
	item: Article,
	category: ArticleCategory,
	addRecommendations: boolean = false,
	maxQuantity: number = 99
) {
	const order = repositoryService._order;
	const articleGroup = createArticleGroup(item, category, order);
	const result = await ModalInfoComponent.show(modalCtrl, articleGroup, false, '', undefined, false, maxQuantity);
	const finalOrder = repositoryService._order;
	await handleModalResult(result, finalOrder, repositoryService, addRecommendations, modalCtrl, item);
}

export function isDoubleMonday(repositoryService: RepositoryService, article: Article): boolean {
	return !!repositoryService.availableDiscounts$.value.find(
		discount => discount.id === environment.doubleMondayId && discount.articles.some(art => art.number === article.number)
	);
}

export function consumerDisplayDiscounts(repositoryService: RepositoryService, article: Article): SmoothrCNCDiscount[] {
	if (!article) {
		return [];
	}
	return repositoryService.availableDiscounts$.value.filter(
		discount => discount.enabled && !!discount.consumerDisplayname && discount.articles.some(art => art.number === article.number)
	);
}
